import React, { useEffect, useState, useRef } from 'react';
import { useEditorStore } from '../../store/useEditorStore';
import { Point2D } from '../../types';
import { Check, X } from 'lucide-react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';

interface MeasurementOverlayProps {
  width: number;
  height: number;
  zoom: number;
  offset: Point2D;
}

const MeasurementOverlay: React.FC<MeasurementOverlayProps> = ({ width, height, zoom, offset }) => {
  const { t } = useTranslation();
  const overlayRef = useRef<HTMLCanvasElement>(null);
  const {
    isMeasuring,
    currentMeasurement,
    completedMeasurements,
    measurementPreviewPoint,
    addMeasurementPoint,
    updateMeasurementPreview,
    completeMeasurement,
    cancelMeasurement,
    deleteMeasurement,
    startMeasurement,
    project,
  } = useEditorStore();

  const [mousePos, setMousePos] = useState<Point2D | null>(null);
  const prevMeasurementLengthRef = useRef<string>('');
  const prevWorldPointRef = useRef<Point2D | null>(null);

  // 测量点变化时打印计算详情（避免重复打印）
  useEffect(() => {
    if (!currentMeasurement || currentMeasurement.length < 2) return;

    // 检查是否是新的测量（通过点的数量变化或点位置变化）
    const currentLength = currentMeasurement.length.toString() +
      (currentMeasurement.length > 0 ? `_${currentMeasurement[currentMeasurement.length - 1].x.toFixed(0)}_${currentMeasurement[currentMeasurement.length - 1].y.toFixed(0)}` : '');

    if (currentLength !== prevMeasurementLengthRef.current) {
      prevMeasurementLengthRef.current = currentLength;

      // 只读取必要的值，避免整个 project 对象
      const calibrationScale = project?.mapConfig?.calibrationScale || 10;
      const isAutoCalibrated = project?.mapConfig?.autoCalibrated || false;
      const lastPoint = currentMeasurement[currentMeasurement.length - 1];
      const prevPoint = currentMeasurement[currentMeasurement.length - 2];
      const worldPixelLength = Math.sqrt(
        Math.pow(lastPoint.x - prevPoint.x, 2) +
        Math.pow(lastPoint.y - prevPoint.y, 2)
      );

      // 如果是自动校准，世界坐标直接就是厘米
      const resultCm = isAutoCalibrated ? worldPixelLength : worldPixelLength / calibrationScale;

      console.log('📏 测量计算详情:', {
        point1: prevPoint,
        point2: lastPoint,
        worldPixelLength: worldPixelLength.toFixed(4),
        calibrationScale: calibrationScale.toFixed(4),
        resultCm: resultCm.toFixed(2),
        resultM: (resultCm / 100).toFixed(2),
        isAutoCalibrated,
      });
    }
  }, [currentMeasurement, project?.mapConfig?.calibrationScale, project?.mapConfig?.autoCalibrated]);

  // 将屏幕坐标转换为世界坐标（考虑缩放和偏移）
  const screenToWorld = (screenPoint: Point2D): Point2D => {
    return {
      x: (screenPoint.x - offset.x) / zoom,
      y: (screenPoint.y - offset.y) / zoom,
    };
  };

  // 将世界坐标转换为屏幕坐标
  const worldToScreen = (worldPoint: Point2D): Point2D => {
    return {
      x: worldPoint.x * zoom + offset.x,
      y: worldPoint.y * zoom + offset.y,
    };
  };

  const calculatePixelDistance = (point1: Point2D, point2: Point2D): number => {
    return Math.sqrt(
      Math.pow(point2.x - point1.x, 2) + Math.pow(point2.y - point1.y, 2)
    );
  };

  const calculateRealLength = (point1: Point2D, point2: Point2D): number => {
    const calibrationScale = project?.mapConfig?.calibrationScale || 10; // 默认10px/cm
    const pixelLength = calculatePixelDistance(point1, point2);

    // 如果是自动校准，世界坐标直接就是厘米，不需要再除以 calibrationScale
    if (project?.mapConfig?.autoCalibrated) {
      return pixelLength; // 直接返回世界坐标距离（厘米）
    }

    return pixelLength / calibrationScale; // 厘米
  };

  // 处理键盘快捷键
  useEffect(() => {
    if (!isMeasuring) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        cancelMeasurement();
        message.info(t('measurement.cancelled'));
      } else if (e.key === 'Enter') {
        if (currentMeasurement && currentMeasurement.length >= 2) {
          completeMeasurement();
          message.success(t('measurement.finished'));
          // 完成后立即开始新的测量
          setTimeout(() => startMeasurement(), 100);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isMeasuring, currentMeasurement, completeMeasurement, cancelMeasurement]);

  // 显示未校准警告
  useEffect(() => {
    if (isMeasuring && !project?.mapConfig?.isCalibrated) {
      message.warning(t('measurement.notCalibrated'));
    }
  }, [isMeasuring, project?.mapConfig?.isCalibrated]);

  // 绘制测量线
  useEffect(() => {
    if (!overlayRef.current) return;

    const canvas = overlayRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const render = () => {
      ctx.clearRect(0, 0, width, height);

      // 绘制已完成的测量线（绿色）
      completedMeasurements.forEach((measurement) => {
        if (measurement.points.length < 2) return;

        // 将世界坐标转换为屏幕坐标
        const screenPoints = measurement.points.map(worldToScreen);

        ctx.save();
        ctx.strokeStyle = '#10b981'; // 绿色
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(screenPoints[0].x, screenPoints[0].y);
        for (let i = 1; i < screenPoints.length; i++) {
          ctx.lineTo(screenPoints[i].x, screenPoints[i].y);
        }
        ctx.stroke();

        // 绘制测量点
        screenPoints.forEach((point) => {
          // 根据zoom动态调整点大小，确保缩小时也能清晰可见
          const baseRadius = 4;
          const radius = zoom < 1 ? baseRadius / zoom : baseRadius;
          const clampedRadius = Math.min(Math.max(radius, baseRadius), 10); // 限制范围 4-10px
          
          ctx.fillStyle = '#10b981';
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.arc(point.x, point.y, clampedRadius, 0, 2 * Math.PI);
          ctx.fill();
          ctx.stroke();
        });

        // 绘制每段长度标注（带外发光效果）
        for (let i = 0; i < screenPoints.length - 1; i++) {
          const p1 = screenPoints[i];
          const p2 = screenPoints[i + 1];
          const midX = (p1.x + p2.x) / 2;
          const midY = (p1.y + p2.y) / 2;
          const length = measurement.segmentLengths[i];
          const text = `${length.toFixed(2)} cm`;

          // 绘制外发光效果（多层阴影）
          ctx.font = '12px Arial';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          
          // 外发光 - 白色阴影
          ctx.shadowColor = 'rgba(255, 255, 255, 0.9)';
          ctx.shadowBlur = 4;
          ctx.fillStyle = '#ffffff';
          for (let j = 0; j < 3; j++) {
            ctx.fillText(text, midX, midY - 4);
          }
          
          // 重置阴影
          ctx.shadowColor = 'transparent';
          ctx.shadowBlur = 0;
          
          // 绘制文字本体
          ctx.fillStyle = '#1f2937';
          ctx.fillText(text, midX, midY - 4);
        }

        // 绘制总长度标注（在末端，带外发光）
        const lastPoint = screenPoints[screenPoints.length - 1];
        const totalText = `${t('measurement.total')}: ${measurement.totalLength.toFixed(2)} cm`;

        ctx.font = 'bold 12px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        
        // 外发光 - 绿色阴影
        ctx.shadowColor = 'rgba(16, 185, 129, 0.8)';
        ctx.shadowBlur = 6;
        ctx.fillStyle = '#10b981';
        for (let j = 0; j < 3; j++) {
          ctx.fillText(totalText, lastPoint.x, lastPoint.y + 18);
        }
        
        // 重置阴影
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
        
        // 绘制文字本体
        ctx.fillStyle = '#ffffff';
        ctx.fillText(totalText, lastPoint.x, lastPoint.y + 18);

        ctx.restore();
      });

      // 绘制当前正在测量的线（蓝色）
      if (isMeasuring && currentMeasurement && currentMeasurement.length > 0) {
        // 将世界坐标转换为屏幕坐标
        const screenPoints = currentMeasurement.map(worldToScreen);
        
        ctx.save();
        ctx.strokeStyle = '#3b82f6'; // 蓝色
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(screenPoints[0].x, screenPoints[0].y);
        for (let i = 1; i < screenPoints.length; i++) {
          ctx.lineTo(screenPoints[i].x, screenPoints[i].y);
        }
        ctx.stroke();

        // 绘制预览线（虚线）
        if (measurementPreviewPoint && screenPoints.length > 0) {
          const lastPoint = screenPoints[screenPoints.length - 1];
          ctx.strokeStyle = '#3b82f6';
          ctx.lineWidth = 1;
          ctx.setLineDash([5, 5]);
          ctx.beginPath();
          ctx.moveTo(lastPoint.x, lastPoint.y);
          ctx.lineTo(measurementPreviewPoint.x, measurementPreviewPoint.y);
          ctx.stroke();
          ctx.setLineDash([]);
        }

        // 绘制测量点
        screenPoints.forEach((point) => {
          // 根据zoom动态调整点大小
          const baseRadius = 4;
          const radius = zoom < 1 ? baseRadius / zoom : baseRadius;
          const clampedRadius = Math.min(Math.max(radius, baseRadius), 10);
          
          ctx.fillStyle = '#3b82f6';
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.arc(point.x, point.y, clampedRadius, 0, 2 * Math.PI);
          ctx.fill();
          ctx.stroke();
        });

        // 绘制每段长度标注（带外发光效果）
        for (let i = 0; i < screenPoints.length - 1; i++) {
          const p1 = screenPoints[i];
          const p2 = screenPoints[i + 1];
          const midX = (p1.x + p2.x) / 2;
          const midY = (p1.y + p2.y) / 2;
          
          // 使用世界坐标计算长度
          const worldP1 = currentMeasurement[i];
          const worldP2 = currentMeasurement[i + 1];
          const length = calculateRealLength(worldP1, worldP2);
          const text = `${length.toFixed(2)} cm`;

          // 绘制外发光效果
          ctx.font = '12px Arial';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          
          // 外发光 - 白色阴影
          ctx.shadowColor = 'rgba(255, 255, 255, 0.9)';
          ctx.shadowBlur = 4;
          ctx.fillStyle = '#ffffff';
          for (let j = 0; j < 3; j++) {
            ctx.fillText(text, midX, midY - 4);
          }
          
          // 重置阴影
          ctx.shadowColor = 'transparent';
          ctx.shadowBlur = 0;
          
          // 绘制文字本体
          ctx.fillStyle = '#1f2937';
          ctx.fillText(text, midX, midY - 4);
        }

        ctx.restore();
      }

      // 绘制鼠标光标
      if (mousePos && isMeasuring) {
        ctx.save();
        // 根据zoom动态调整光标大小
        const baseInnerRadius = 3;
        const baseOuterRadius = 5;
        const innerRadius = zoom < 1 ? baseInnerRadius / zoom : baseInnerRadius;
        const outerRadius = zoom < 1 ? baseOuterRadius / zoom : baseOuterRadius;
        const clampedInnerRadius = Math.min(Math.max(innerRadius, baseInnerRadius), 6);
        const clampedOuterRadius = Math.min(Math.max(outerRadius, baseOuterRadius), 8);
        
        ctx.fillStyle = '#3b82f6';
        ctx.beginPath();
        ctx.arc(mousePos.x, mousePos.y, clampedInnerRadius, 0, 2 * Math.PI);
        ctx.fill();

        ctx.strokeStyle = '#93c5fd';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(mousePos.x, mousePos.y, clampedOuterRadius, 0, 2 * Math.PI);
        ctx.stroke();
        ctx.restore();
      }

      requestAnimationFrame(render);
    };

    const animationId = requestAnimationFrame(render);
    return () => cancelAnimationFrame(animationId);
  }, [
    isMeasuring,
    currentMeasurement,
    completedMeasurements,
    measurementPreviewPoint,
    mousePos,
    width,
    height,
  ]);

  if (!isMeasuring && completedMeasurements.length === 0) return null;

  return (
    <>
      {/* Overlay Canvas */}
      <canvas
        ref={overlayRef}
        width={width}
        height={height}
        className="absolute top-0 left-0"
        style={{
          zIndex: 100,
          pointerEvents: 'none',
        }}
      />

      {/* 交互层 */}
      {isMeasuring && (
        <div
          className="absolute top-0 left-0"
          style={{
            width: `${width}px`,
            height: `${height}px`,
            zIndex: 101,
            cursor: 'crosshair',
          }}
          onMouseMove={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;
            const point = { x, y };

            setMousePos(point);
            updateMeasurementPreview(point);
          }}
          onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;
            const screenPoint = { x, y };

            // 转换为世界坐标存储
            const worldPoint = screenToWorld(screenPoint);

            // 手动计算验证
            const manualWorldX = (screenPoint.x - offset.x) / zoom;
            const manualWorldY = (screenPoint.y - offset.y) / zoom;

            console.log('🖱️ 点击测量:', {
              screenPoint,
              worldPoint,
              manualWorld: { x: manualWorldX, y: manualWorldY },
              zoom,
              offset,
              calibrationScale: project?.mapConfig?.calibrationScale,
              // 额外计算：使用世界坐标计算像素距离
              pixelLength: Math.sqrt(
                Math.pow(manualWorldX - (prevWorldPointRef.current?.x || manualWorldX), 2) +
                Math.pow(manualWorldY - (prevWorldPointRef.current?.y || manualWorldY), 2)
              ).toFixed(2),
              prevWorldPoint: prevWorldPointRef.current,
            });

            prevWorldPointRef.current = worldPoint;

            // 限制最大点数为50
            if (currentMeasurement && currentMeasurement.length >= 50) {
              message.warning(t('measurement.maxPoints'));
              return;
            }

            addMeasurementPoint(worldPoint);
          }}
        />
      )}

      {/* 提示文字 */}
      {isMeasuring && (
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-[102] pointer-events-none">
          <div className="bg-primary-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-3">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
              <span className="font-medium">
                {!currentMeasurement || currentMeasurement.length === 0
                  ? t('measurement.clickToStart')
                  : t('measurement.continueOrFinish')}
              </span>
            </div>
            <div className="text-xs opacity-75">{t('measurement.cancelOrFinish')}</div>
          </div>
        </div>
      )}

      {/* 当前测量的按钮 */}
      {isMeasuring && currentMeasurement && currentMeasurement.length >= 2 && (() => {
        const lastWorldPoint = currentMeasurement[currentMeasurement.length - 1];
        const lastScreenPoint = worldToScreen(lastWorldPoint);
        return (
        <div
          className="absolute z-[102] flex gap-2"
          style={{
            left: `${lastScreenPoint.x + 10}px`,
            top: `${lastScreenPoint.y - 30}px`,
          }}
        >
          <button
            className="bg-green-500 hover:bg-green-600 text-white rounded-full p-1 shadow-lg transition-colors"
            onClick={() => {
              completeMeasurement();
              message.success(t('measurement.finished'));
              // 完成后立即开始新的测量
              setTimeout(() => startMeasurement(), 100);
            }}
            title={t('measurement.finish')}
          >
            <Check size={12} />
          </button>
          <button
            className="bg-red-500 hover:bg-red-600 text-white rounded-full p-1 shadow-lg transition-colors"
            onClick={() => {
              cancelMeasurement();
              message.info(t('measurement.cancelled'));
            }}
            title={t('measurement.cancelHint')}
          >
            <X size={12} />
          </button>
        </div>
        );
      })()}

      {/* 已完成测量线的删除按钮 */}
      {completedMeasurements.map((measurement) => {
        if (measurement.points.length === 0) return null;
        const lastWorldPoint = measurement.points[measurement.points.length - 1];
        const lastScreenPoint = worldToScreen(lastWorldPoint);
        
        // 计算总计文本的宽度，按钮显示在文本右侧
        const totalText = `${t('measurement.total')}: ${measurement.totalLength.toFixed(2)} cm`;
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.font = 'bold 12px Arial';
          const textWidth = ctx.measureText(totalText).width;
          
          return (
            <button
              key={measurement.id}
              className="absolute z-[102] bg-red-500 hover:bg-red-600 text-white rounded-full p-1 shadow-lg transition-colors"
              style={{
                left: `${lastScreenPoint.x + textWidth / 2 + 10}px`,
                top: `${lastScreenPoint.y + 12}px`,
              }}
              onClick={() => {
                deleteMeasurement(measurement.id);
                message.info(t('measurement.deleted'));
              }}
              title={t('measurement.deleteLine')}
            >
              <X size={12} />
            </button>
          );
        }
        return null;
      })}
    </>
  );
};

export default MeasurementOverlay;
